import _ from "lodash";
import Cache from "core/cache/index";
import { getEnabledElement } from "component/toolManager/enabledElements.js";
import calculateSUV from "component/util/calculateSUV";
import makeKey from "core/cache/KeyGen";
import { getPresetOrientation } from "./core/presetImageOrientation";

const CANVAS_CSS_CLASS = "viewer-canvas";

/**
 * Check if the angle is rotated
 * @param {Number} rotation the rotation angle
 * @returns {Boolean} true if the angle is rotated; Otherwise, false.
 * @memberof Internal
 */
function isRotated(rotation) {
  return !(
    rotation === null ||
    rotation === undefined ||
    rotation === 0 ||
    rotation === 180
  );
}

/**
 * Retrieves the current image dimensions given an enabled element
 *
 * @param {any} image The  image.
 * @param {Number} rotation Optional. The rotation angle of the image.
 * @return {{width:Number, height:Number}} The Image dimensions
 * @memberof Internal
 */
function getImageSize(image, rotation = null) {
  if (isRotated(rotation)) {
    return {
      height: image.width,
      width: image.height,
    };
  }

  return {
    width: image.width,
    height: image.height,
  };
}

/**
 * Calculates the horizontal, vertical and minimum scale factor for an image
   @param {{width, height}} windowSize The window size where the image is displayed. This can be any HTML element or structure with a width, height fields (e.g. canvas).
 * @param {any} image The  image object
 * @param {Number} rotation Optional. The rotation angle of the image.
 * @return {{horizontalScale, verticalScale, scaleFactor}} The calculated horizontal, vertical and minimum scale factor
 * @memberof Internal
 */
function getImageFitScale(windowSize, image, rotation = null) {
  const imageSize = getImageSize(image, rotation);
  const rowPixelSpacing = image.rowPixelSpacing || 1;
  const columnPixelSpacing = image.columnPixelSpacing || 1;
  let verticalRatio = 1;
  let horizontalRatio = 1;

  if (rowPixelSpacing < columnPixelSpacing) {
    horizontalRatio = columnPixelSpacing / rowPixelSpacing;
  } else {
    // even if they are equal we want to calculate this ratio (the ration might be 0.5)
    verticalRatio = rowPixelSpacing / columnPixelSpacing;
  }

  const verticalScale = windowSize.height / imageSize.height / verticalRatio;
  const horizontalScale = windowSize.width / imageSize.width / horizontalRatio;

  // Fit image to window
  return {
    verticalScale,
    horizontalScale,
    scaleFactor: Math.min(horizontalScale, verticalScale),
  };
}

/**
 * Create a canvas and append it to the element
 *
 * @param {HTMLElement} element An HTML Element
 * @return {HTMLElement} canvas A Canvas DOM element
 */
function createCanvas(element) {
  const canvas = document.createElement("canvas");

  canvas.style.display = "block";
  canvas.classList.add(CANVAS_CSS_CLASS);
  element.appendChild(canvas);

  return canvas;
}

/**
 * Create a canvas or returns the one that already exists for a given element
 *
 * @param {HTMLElement} element An HTML Element
 * @return {HTMLElement} canvas A Canvas DOM element
 */
export default function getCanvas(element) {
  const selector = `canvas.${CANVAS_CSS_CLASS}`;

  return element.querySelector(selector) || createCanvas(element);
}

/**
 * Creates a new viewport object containing default values
 *
 * @returns {Viewport} viewport object
 * @memberof Internal
 */
function createViewport() {
  const displayedArea = createDefaultDisplayedArea();
  return {
    scale: 1,
    translation: {
      x: 0,
      y: 0,
    },
    voi: {
      windowWidth: undefined,
      windowCenter: undefined,
    },
    invert: false,
    pixelReplication: false,
    rotation: 0,
    hflip: false,
    vflip: false,
    modalityLUT: undefined,
    voiLUT: undefined,
    colormap: undefined,
    labelmap: false,
    imageOrientation: [1, 0, 0, 0, 1, 0], // axial image orientation vector
    displayedArea,
  };
}

/**
 * Creates the default displayed area.
 * C.10.4 Displayed Area Module: This Module describes Attributes required to define a Specified Displayed Area space.
 *
 * @returns {tlhc: {x,y}, brhc: {x, y},rowPixelSpacing: Number, columnPixelSpacing: Number, presentationSizeMode: Number} displayedArea object
 * @memberof Internal
 */

function createDefaultDisplayedArea() {
  return {
    // Top Left Hand Corner
    tlhc: {
      x: 1,
      y: 1,
    },
    // Bottom Right Hand Corner
    brhc: {
      x: 1,
      y: 1,
    },
    rowPixelSpacing: 1,
    columnPixelSpacing: 1,
    presentationSizeMode: "NONE",
  };
}

/**
 * Returns a default viewport for display the specified image on the specified
 * enabled element.  The default viewport is fit to window
 *
 * @param {HTMLElement} element The DOM element enabled for
 * @param {Image} image A  Image Object
 *
 * @returns {Viewport} The default viewport for the image
 */
function getDefaultViewportForImage(element, image) {
  return getDefaultViewport(element.canvas, image);
}

/**
 * Creates a new viewport object containing default values for the image and canvas
 *
 * @param {HTMLElement} canvas A Canvas DOM element
 * @param {Image} image A  Image Object
 * @returns {Viewport} viewport object
 * @memberof Internal
 */
function getDefaultViewport(canvas, image) {
  if (canvas === undefined) {
    throw new Error(
      "getDefaultViewport: parameter canvas must not be undefined"
    );
  }

  if (image === undefined) {
    return createViewport();
  }

  const modality = _.get(image, "modality", "");
  if (modality.toLowerCase() === "pt") {
    return getDefaultViewportPT(canvas, image);
  }
  return getDefaultViewportAsOthers(canvas, image);
}

function getDefaultViewportPT(
  canvas,
  image,
  percent = 25,
  presetAxisName = "axial"
) {
  // PT 영상 출력 대상 하위 퍼센트를 Window 포멧으로 변경
  let metadataCache = new Cache.Metadata();
  const dataId = makeKey(image.seriesInstanceUID, image.modality);
  const seriesMinMax = metadataCache.getSeriesMinMax(dataId);
  const maxPixelValue = seriesMinMax["max"];
  // @FIXME
  const seriesMaxRescaleSlope =
    metadataCache.getSeriesMaxRescaleSlope(dataId) || 1;
  const supremumUnit =
    ((maxPixelValue * percent) / 100) * seriesMaxRescaleSlope;
  const infimumUnit = 0 * seriesMaxRescaleSlope;

  const windowCenter = (supremumUnit + infimumUnit) / 2;
  const windowWidth = Math.abs(supremumUnit - infimumUnit);

  // Fit image to window
  const scale = getImageFitScale(canvas, image, 0).scaleFactor;
  const orientationVec = getPresetOrientation(presetAxisName) || [
    1,
    0,
    0,
    0,
    1,
    0,
  ]; // axial orientation vector
  return {
    scale,
    translation: {
      x: 0,
      y: 0,
    },
    voi: {
      windowWidth: windowWidth,
      windowCenter: windowCenter,
    },
    imageOrientation: new Float32Array(orientationVec),
    invert: image.invert,
    pixelReplication: false,
    rotation: 0,
    hflip: false,
    vflip: false,
    modalityLUT: image.modalityLUT,
    voiLUT: image.voiLUT,
    colormap: image.colormap,
    labelmap: Boolean(image.labelmap),
    imagePosition: [0, 0, 0], // FIXME : 임시 설정.
    displayedArea: {
      tlhc: {
        x: 1,
        y: 1,
      },
      brhc: {
        x: image.columns,
        y: image.rows,
      },
      rowPixelSpacing:
        image.rowPixelSpacing === undefined ? 1 : image.rowPixelSpacing,
      columnPixelSpacing:
        image.columnPixelSpacing === undefined ? 1 : image.columnPixelSpacing,
      presentationSizeMode: "NONE",
    },
  };
}

function getDefaultViewportAsOthers(canvas, image, presetAxisName = "axial") {
  // Fit image to window
  const scale = getImageFitScale(canvas, image, 0).scaleFactor;
  const orientationVec = getPresetOrientation(presetAxisName) || [
    1,
    0,
    0,
    0,
    1,
    0,
  ]; // axial orientation vector
  return {
    scale,
    translation: {
      x: 0,
      y: 0,
    },
    voi: {
      windowWidth: image.windowWidth,
      windowCenter: image.windowCenter,
    },
    imageOrientation: new Float32Array(orientationVec),
    invert: image.invert,
    pixelReplication: false,
    rotation: 0,
    hflip: false,
    vflip: false,
    modalityLUT: image.modalityLUT,
    voiLUT: image.voiLUT,
    colormap: image.colormap,
    labelmap: Boolean(image.labelmap),
    imagePosition: [0, 0, 0], // FIXME : 임시 설정.
    displayedArea: {
      tlhc: {
        x: 1,
        y: 1,
      },
      brhc: {
        x: image.columns,
        y: image.rows,
      },
      rowPixelSpacing:
        image.rowPixelSpacing === undefined ? 1 : image.rowPixelSpacing,
      columnPixelSpacing:
        image.columnPixelSpacing === undefined ? 1 : image.columnPixelSpacing,
      presentationSizeMode: "NONE",
    },
  };
}

/**
 * Retrieves the viewport for the specified enabled element
 *
 * @param {HTMLElement} element The DOM element enabled for
 * @returns {Viewport|undefined} The  Viewport settings for this element, if they exist. Otherwise, undefined
 * @memberof ViewportSettings
 */
function getViewport(element) {
  let enabledElement = element;

  // 임시
  if (!_.get(enabledElement, "viewport")) {
    enabledElement = getEnabledElement(element);
  }

  const viewport = enabledElement.viewport;

  if (viewport === undefined) {
    return;
  }

  // Return a copy of the viewport
  return Object.assign({}, viewport);
}

export {
  createCanvas,
  getCanvas,
  createViewport,
  createDefaultDisplayedArea,
  getDefaultViewportForImage,
  getDefaultViewport,
  getViewport,
};
